import { useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react';
import { BiGame, BiLogOut, BiRefresh, BiUser } from 'react-icons/bi';
import { ImExit } from 'react-icons/im';

import AvatarEditor from 'components/AvatarEditor';
import { useAuth, useBasic, useModal } from 'contexts';
import { AiOutlineTrophy } from 'react-icons/ai';
import { BsCoin } from 'react-icons/bs';
import { FaCog, FaMedal } from 'react-icons/fa';
import { IoSettings } from 'react-icons/io5';
import { MdSettings } from 'react-icons/md';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { fetchDataURL, resizeImage, updateUserProfile, uploadPicture } from 'services/profile.service';
import { getRegisterTypes, getCertificado, getCompletedCourses, getAllChallengesWithBadge } from 'services';
import { formatPoints } from 'utils';
import { GrCertificate } from 'react-icons/gr';
import { ModalPrintCertificate } from 'components/ModalPrintCertificate';
import AvatarComponent from '../NewAvatar/index';
// Função para extrair e limpar nome de email
const getDisplayName = (name: string | undefined): string => {
  if (!name) return '';

  // Se for email, pega apenas a parte antes do @
  const displayName = name.includes('@') ? name.split('@')[0] : name;

  // Remove . - _ e números
  const cleanName = displayName.replace(/[.\-_0-9]/g, ' ').trim();

  return cleanName || displayName; // Retorna o nome limpo ou original se ficar vazio
};

export default function UserInfo() {
  const navigate = useNavigate();
  const location = useLocation();

  const { userPoints } = useBasic();
  const { user, refreshUser, signOut, profileUser } = useAuth();
  const { appearance } = useBasic();
  const [avatarLoading, setAvatarLoading] = useState(false);
  const avatarEditor = useRef<any>();
  const [registerTypes, setRegisterTypes] = useState<any>({});

  const [showPrint, setShowPrint] = useState<boolean>(false);
  const { showModal, closeModal } = useModal();
  const [earnedBadges, setEarnedBadges] = useState<any[]>([]);

  useEffect(() => {
    if (!user) return;
    getAllChallengesWithBadge().then((allBadges: any[]) => {
      const userChallengeIds = Object.keys(user.challenges || {});
      const earned = allBadges.filter((b: any) => userChallengeIds.includes(b._id) && b.techniques?.includes('GT02'));
      setEarnedBadges(earned);
    });
  }, [user]);

  useEffect(() => {
    if (user && !user.extra.acceptTerms && location.pathname !== '/configuracoes/termos-de-uso') {
      navigate('/configuracoes/termos-de-uso', { replace: true });
    }
  }, [user, location.pathname, navigate]);

  const onOpenAvatarEditor = () => {
    if (avatarLoading) return;
    avatarEditor.current?.open();
  };

  async function updateAvatarImage(dataURI: string) {
    try {
      const { uploads } = await uploadPicture(dataURI);
      const [{ url }] = uploads;
      if (url) {
        const url = uploads[0].url;
        const payload = {
          _id: user?._id,
          image: {
            small: { url },
            medium: { url },
            original: { url },
          },
        };

        const updatedUser = await updateUserProfile(payload);
        refreshUser();

        // console.log(updatedUser);
      }
    } catch (e) {
      console.log(e);
    }
  }

  async function prepareToUpdateAvatar(url: string) {
    if (!user) return;

    setAvatarLoading(true);

    const imgResult = await fetchDataURL(url);
    if (imgResult) {
      const imgResized = await resizeImage(imgResult);
      await updateAvatarImage(imgResized);
    }
  }

  const renderAlternativeNav = useCallback(() => {
    if (!appearance || appearance.navigation?.displayMenu) {
      return;
    }

    return (
      <div className="flex-1 flex pl-3 gap-3">
        <Link to="/games/runner" className="ring-2 ring-blue-300 text-blue-600 inline-flex rounded-xl p-3 items-center gap-3">
          <BiGame size={30} /> <b>GAME</b>
        </Link>
        <Link to="/ranking" className="ring-2 ring-yellow-300 text-yellow-600 inline-flex rounded-xl p-3 items-center gap-3">
          <AiOutlineTrophy size={30} /> <b>RANKING</b>
        </Link>
      </div>
    );
  }, [appearance]);

  // useEffect(() => {
  //   if (user) {
  //     checkCompletedCourses();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [user]);

  // async function checkCompletedCourses() {
  //   if (!user) return;
  //   const response = await getCompletedCourses({}, 'items=0-1000');

  //   const hasCourseCompleted = response?.data?.length > 0 ? true : false;
  //   setShowPrint(hasCourseCompleted);
  // }

  // async function printCertified() {
  //   const certificado = await getCertificado();
  //   showModal('text', <ModalPrintCertificate certifield={certificado} profiles={profileUser?.roles}/>, 'text-black');
  // }

  return (
    <>
      <div id="user-info" className="hidden lg:block relative">
        <div className="lg:flex items-center justify-between">
          <div className="lg:flex items-center gap-3">
            <div className="hidden lg:block avatar w-16 h-16 rounded-full">
              <div className="w-full h-full flex items-center justify-center mx-3">
                {avatarLoading ? (
                  <span>
                    <BiRefresh size={32} className="animate-spin" />
                  </span>
                ) : (
                  <AvatarComponent size={84} />
                )}
              </div>
            </div>
            <div className="user-gamification flex flex-col justify-center m-8">
              <h2 className="text-[#181b1a] text-base font-bold leading-tight mb-1 flex items-center gap-2">
                {getDisplayName(user?.name)}
                {earnedBadges.length > 0 && (
                  <span className="flex items-center gap-1">
                    {earnedBadges.map(badge => (
                      <img
                        key={badge._id}
                        src={badge.badge.small.url}
                        alt={badge.challenge}
                        title={badge.challenge}
                        className="w-7 h-7 rounded-full object-contain bg-gray-100"
                      />
                    ))}
                  </span>
                )}
              </h2>

              <div className="flex items-center gap-3 text-sm text-gray-500">
                {userPoints?.map(point => (
                  <span key={`${point._id}_${point.total}`} className="whitespace-nowrap">
                    {point.category} <strong className="text-[#fb6d3a]">{formatPoints(point.total)} </strong>
                  </span>
                ))}

                {!!user?.level?.level && <span className="whitespace-nowrap text-[#fb6d3a] font-bold">{user.level.level} </span>}
              </div>
            </div>
          </div>
          {renderAlternativeNav()}

          <div className="logout text-right lg:flex space-x-3">
            <button type="button" className="text-indigo-400 hidden lg:flex" onClick={() => navigate('/configuracoes')}>
              <FaCog size={35} />
            </button>
            <button onClick={() => signOut()} type="button" className="text-red-600 opacity-50" title="Sair">
              {/* <BiLogOut size={44} /> */}
              <ImExit size={35} />
            </button>
          </div>
        </div>
      </div>
      <AvatarEditor
        ref={avatarEditor}
        onRenderStart={() => {
          setAvatarLoading(true);
        }}
        onRenderFinished={url => {
          prepareToUpdateAvatar(url);
        }}
      />
    </>
  );
}
